/* Sura — product image hover zoom (<sura-zoom>)
   Modes:
     panel — lens over the image, magnified view in a panel beside the gallery
     inner — the image itself magnifies under the pointer
     lens  — a small magnifier square follows the pointer
   Panel mode falls back to inner when there isn't room beside the gallery. */
if (!customElements.get('sura-zoom')) {
  const MIN_PANEL_SPACE = 320;

  class SuraZoom extends HTMLElement {
    connectedCallback() {
      // Desktop pointers only: hover zoom means nothing on a touch screen
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      this.gallery = this.closest('.sura-pdp__gallery') || this.parentElement;
      this.stage = this.parentElement?.querySelector('.sura-pdp__stage');
      if (!this.stage) return;

      this.level = Math.max(110, parseInt(this.dataset.level, 10) || 200) / 100;
      this.mode = this.dataset.mode || 'panel';
      this.panelWidth = parseInt(this.dataset.panelWidth, 10) || 520;

      this.gallery.classList.add('sura-zoom-host');
      this.stage.classList.add('is-zoomable');

      if (this.mode === 'panel') this.buildPanel();

      this.stage.querySelectorAll('[data-zoom-src]').forEach((slide) => this.setup(slide));
    }

    buildPanel() {
      this.panel = document.createElement('div');
      this.panel.className = 'sura-zoom__panel';
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.hidden = true;
      this.gallery.appendChild(this.panel);
    }

    /* Panel mode needs room to the right of the gallery, otherwise zoom inside the image */
    effectiveMode() {
      if (this.mode !== 'panel') return this.mode;
      const rect = this.gallery.getBoundingClientRect();
      const space = window.innerWidth - rect.right - 24;
      return space >= MIN_PANEL_SPACE ? 'panel' : 'inner';
    }

    setup(slide) {
      slide.classList.add('is-zoomable');

      const layer = document.createElement('span');
      layer.className = 'sura-zoom__layer';
      layer.setAttribute('aria-hidden', 'true');
      slide.appendChild(layer);

      let loaded = false;
      const load = () => {
        if (loaded) return;
        loaded = true;
        const src = slide.dataset.zoomSrc;
        const img = new Image();
        img.src = src;
        const paint = () => {
          layer.style.backgroundImage = `url("${src}")`;
          if (this.panel) this.panel.style.backgroundImage = `url("${src}")`;
        };
        if (img.decode) img.decode().then(paint, paint);
        else img.onload = paint;
      };

      slide.addEventListener('pointerenter', () => {
        load();
        this.active = this.effectiveMode();
        slide.dataset.zoomMode = this.active;
        slide.classList.add('is-zooming');

        if (this.active === 'panel' && this.panel) {
          const rect = slide.getBoundingClientRect();
          this.panel.style.backgroundImage = `url("${slide.dataset.zoomSrc}")`;
          this.panel.style.width = `${this.panelWidth}px`;
          this.panel.style.height = `${Math.round(rect.height)}px`;
          this.panel.hidden = false;
        }
      });

      slide.addEventListener('pointermove', (e) => {
        const rect = slide.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const x = Math.min(Math.max(e.clientX - rect.left, 0), rect.width);
        const y = Math.min(Math.max(e.clientY - rect.top, 0), rect.height);
        const zoomW = rect.width * this.level;
        const zoomH = rect.height * this.level;

        if (this.active === 'inner') {
          layer.style.backgroundSize = `${zoomW}px ${zoomH}px`;
          layer.style.backgroundPosition = `${(x / rect.width) * 100}% ${(y / rect.height) * 100}%`;
          return;
        }

        // panel and lens both show a window onto the magnified image
        let winW;
        let winH;
        if (this.active === 'panel') {
          winW = this.panelWidth / this.level;
          winH = rect.height / this.level;
        } else {
          winW = Math.min(rect.width, rect.height) * 0.45;
          winH = winW;
        }
        winW = Math.min(winW, rect.width);
        winH = Math.min(winH, rect.height);

        const winX = Math.min(Math.max(x - winW / 2, 0), rect.width - winW);
        const winY = Math.min(Math.max(y - winH / 2, 0), rect.height - winH);

        layer.style.width = `${winW}px`;
        layer.style.height = `${winH}px`;
        layer.style.transform = `translate(${winX}px, ${winY}px)`;

        if (this.active === 'panel' && this.panel) {
          this.panel.style.backgroundSize = `${zoomW}px ${zoomH}px`;
          this.panel.style.backgroundPosition = `${-winX * this.level}px ${-winY * this.level}px`;
        } else {
          layer.style.backgroundSize = `${zoomW}px ${zoomH}px`;
          layer.style.backgroundPosition = `${-winX * this.level}px ${-winY * this.level}px`;
        }
      });

      const stop = () => {
        slide.classList.remove('is-zooming');
        if (this.panel) this.panel.hidden = true;
      };
      slide.addEventListener('pointerleave', stop);
      slide.addEventListener('pointercancel', stop);
    }
  }

  customElements.define('sura-zoom', SuraZoom);
}
